import React from 'react'
import { ArrowRight } from 'lucide-react'
import { IEndUserCareer } from 'interfaces/CareerInterface'
import { noPreviewImage } from 'utills/image'

export type TakeNextStepCTA = {
  label: string
  description?: string
  onClick: () => void
}

type TakeNextStepCardProps = {
  items: IEndUserCareer[]
  onSelect: (id: string) => void
  className?: string
  minHeight?: number
  subtitle?: string
  emptyCTAs?: TakeNextStepCTA[]
  emptyTitle?: string
  emptySubtitle?: string
}

const TakeNextStepCard: React.FC<TakeNextStepCardProps> = ({
  items,
  onSelect,
  className = '',
  minHeight = 328,
  subtitle = 'Go deeper on your top 3 with more insights from experts.',
  emptyCTAs,
  emptyTitle,
  emptySubtitle,
}) => {
  const isEmpty = items.length === 0
  const showCTAs = isEmpty && !!emptyCTAs && emptyCTAs.length > 0

  return (
    <div
      className={`rounded-md border border-gray-light p-6 flex flex-col gap-5 w-full ${className}`}
      style={{
        minHeight,
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.06), 0 1px 3px 0 rgba(0,0,0,0.1)',
      }}
    >
      <div className="flex flex-col gap-1">
        <h3 className="font-sans text-2xl font-semibold leading-8 tracking-normal text-brand">
          {isEmpty && emptyTitle ? emptyTitle : 'Take the next step'}
        </h3>
        <p className="font-sans text-base font-normal leading-4 tracking-normal text-gray-dark">
          {isEmpty && emptySubtitle ? emptySubtitle : subtitle}
        </p>
      </div>
      <div className="flex flex-col">
        {isEmpty && !showCTAs ? (
          <p className="font-sans text-sm text-gray-medium">No careers yet.</p>
        ) : showCTAs ? (
          emptyCTAs!.map((cta, i) => (
            <button
              key={cta.label}
              type="button"
              onClick={cta.onClick}
              className="flex items-center justify-between gap-3 py-3 hover:opacity-90 text-left"
              style={i > 0 ? { borderTop: '1px solid #E8E8E8' } : undefined}
            >
              <div className="flex flex-col min-w-0">
                <span className="font-sans text-base font-semibold text-brand truncate">
                  {cta.label}
                </span>
                {cta.description && (
                  <span className="font-sans text-xs text-gray-dark truncate">
                    {cta.description}
                  </span>
                )}
              </div>
              <span
                className="flex items-center justify-center text-gray-dark shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: '#E8E8E8',
                }}
                aria-hidden="true"
              >
                <ArrowRight size={16} />
              </span>
            </button>
          ))
        ) : (
          items.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id)}
              className="flex items-center justify-between gap-3 py-3 hover:opacity-90 text-left"
              style={i > 0 ? { borderTop: '1px solid #E8E8E8' } : undefined}
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={c.icon_url || noPreviewImage}
                  alt={c.name}
                  className="w-10 h-10 rounded-full object-cover shrink-0"
                  style={{ border: '1px solid #E8E8E8' }}
                />
                <span className="font-sans text-base font-semibold text-brand truncate">
                  {c.name}
                </span>
              </div>
              <span
                className="flex items-center justify-center text-gray-dark shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: '#E8E8E8',
                }}
                aria-hidden="true"
              >
                <ArrowRight size={16} />
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

export default TakeNextStepCard
